/** Tron 只读 TronWeb + WalletConnect 签名广播 */

import { getChainFromId } from "./config.mjs";
import { base58en, normalizeTronTxResult } from "./tron.mjs";
import { isMetaMaskTronProvider } from './metamaskTron.mjs'
import { isTronWalletConnectProvider } from "../tronWcDetect.mjs";
import { isTronWebProvider, requireTronWeb, resolveTronWebInstance } from "./tron.mjs";
import {
  getTronFullHost,
  getCachedReadOnlyTronWeb,
  setCachedReadOnlyTronWeb,
  invalidateTronWebCache,
  withTronWebRetry,
} from './tronApi.mjs'
import Erc20Abi from "./abis/IERC20.js";
import HieSwapAbi from "./abis/HieSwap.js";
import ExchangeAbi from "./abis/Exchange.js";

let readOnlyTronWeb = null;

function tronFullHost() {
  return getTronFullHost()
}

function resolveTronOwnerBase58(provider) {
  const direct = String(provider?.tronAddressBase58 || '').trim()
  if (direct.startsWith('T')) return direct
  const wcAddr = String(provider?._tronWallet?.address || '').trim()
  if (wcAddr.startsWith('T')) return wcAddr
  const mmAddr = String(provider?._metaMaskTronAdapter?.address || '').trim()
  if (mmAddr.startsWith('T')) return mmAddr
  return ''
}

export async function getReadOnlyTronWeb() {
  const host = tronFullHost()
  const cached = getCachedReadOnlyTronWeb()
  if (cached && cached.fullHost === host) return cached
  if (readOnlyTronWeb && readOnlyTronWeb.fullHost !== host) {
    readOnlyTronWeb = null
    invalidateTronWebCache()
  }
  if (readOnlyTronWeb) return readOnlyTronWeb
  const { TronWeb } = await import("tronweb");
  readOnlyTronWeb = new TronWeb({ fullHost: host });
  readOnlyTronWeb.fullHost = host
  setCachedReadOnlyTronWeb(readOnlyTronWeb)
  return readOnlyTronWeb;
}

export function resolveTronOpsWallet(provider) {
  if (isTronWalletConnectProvider(provider) || isMetaMaskTronProvider(provider)) return null;
  if (isTronWebProvider(provider)) return provider;
  return requireTronWeb();
}

async function signAndBroadcastTronExternal(provider, transaction) {
  const wallet = provider._tronWallet || provider._metaMaskTronAdapter
  const signed = await wallet.signTransaction(transaction)
  return withTronWebRetry(async () => {
    const tw = await getReadOnlyTronWeb()
    return tw.trx.sendRawTransaction(signed)
  })
}

async function buildTriggerSmartContract(from, contractBase58, functionSelector, parameters, feeLimit = 150000000) {
  return withTronWebRetry(async () => {
    const tw = await getReadOnlyTronWeb()
    const built = await tw.transactionBuilder.triggerSmartContract(
      contractBase58,
      functionSelector,
      { feeLimit, callValue: 0 },
      parameters,
      from,
    )
    const transaction = built?.transaction ?? built
    if (!transaction?.raw_data) {
      const err = new Error('TRON_TX_BUILD_FAILED')
      err.code = 'TRON_TX_BUILD_FAILED'
      throw err
    }
    return transaction
  })
}

async function triggerSmartContractExternal(provider, contractBase58, functionSelector, parameters, feeLimit = 150000000) {
  const from = resolveTronOwnerBase58(provider)
  if (!from) {
    const err = new Error('NO_TRON_ACCOUNT')
    err.code = 'NO_PROVIDER'
    throw err
  }
  const transaction = await buildTriggerSmartContract(
    from,
    contractBase58,
    functionSelector,
    parameters,
    feeLimit,
  )
  return signAndBroadcastTronExternal(provider, transaction)
}

export async function tronApproveWithProvider(provider, amount, spender) {
  const chainConf = getChainFromId(95500);
  const tw = resolveTronOpsWallet(provider);
  const spenderHex = spender === chainConf.exchangeContract ? spender : chainConf.contract;
  const contractBase58 = base58en(chainConf.usdt);
  const spenderBase58 = base58en(spenderHex);

  if (isTronWalletConnectProvider(provider) || isMetaMaskTronProvider(provider)) {
    const res = await triggerSmartContractExternal(
      provider,
      contractBase58,
      "approve(address,uint256)",
      [
        { type: "address", value: spenderBase58 },
        { type: "uint256", value: String(amount) },
      ],
    );
    return normalizeTronTxResult(res);
  }

  const usdt = await tw.contract(JSON.parse(JSON.stringify(Erc20Abi)), contractBase58);
  const res = await usdt.approve(spenderBase58, amount).send();
  return normalizeTronTxResult(res);
}

export async function tronSwapWithProvider(provider, token, toChainId, to, amount) {
  const chainConf = getChainFromId(95500);
  const tw = resolveTronOpsWallet(provider);

  if (isTronWalletConnectProvider(provider) || isMetaMaskTronProvider(provider)) {
    const res = await triggerSmartContractExternal(
      provider,
      base58en(chainConf.contract),
      "crossChain(address,uint256,address,uint256)",
      [
        { type: "address", value: base58en(token) },
        { type: "uint256", value: String(toChainId) },
        { type: "address", value: to },
        { type: "uint256", value: String(amount) },
      ],
      300000000,
    );
    return normalizeTronTxResult(res);
  }

  const contract = await tw.contract(JSON.parse(JSON.stringify(HieSwapAbi)), base58en(chainConf.contract));
  const res = await contract.crossChain(base58en(token), toChainId, to, amount).send();
  return normalizeTronTxResult(res);
}

export async function tronExchangeWithProvider(provider, token, toChainId, to, amount) {
  const chainConf = getChainFromId(95500);
  const tw = resolveTronOpsWallet(provider);

  if (isTronWalletConnectProvider(provider) || isMetaMaskTronProvider(provider)) {
    const res = await triggerSmartContractExternal(
      provider,
      base58en(chainConf.exchangeContract),
      "crossChain(address,uint256,address,uint256)",
      [
        { type: "address", value: base58en(token) },
        { type: "uint256", value: String(toChainId) },
        { type: "address", value: to },
        { type: "uint256", value: String(amount) },
      ],
      300000000,
    );
    return normalizeTronTxResult(res);
  }

  const contract = await tw.contract(JSON.parse(JSON.stringify(ExchangeAbi)), base58en(chainConf.exchangeContract));
  const res = await contract.crossChain(base58en(token), toChainId, to, amount).send();
  return normalizeTronTxResult(res);
}

export function hasTronWalletForOps(provider) {
  if (isTronWalletConnectProvider(provider) || isMetaMaskTronProvider(provider)) return true;
  try {
    return !!resolveTronWebInstance() || isTronWebProvider(provider);
  } catch (_e) {
    return isTronWebProvider(provider);
  }
}
